<template>
    <div
        class="t-date-picker date-picker-container"
        :class="{
            'enable-clear': enableClear,
            'date-picker-shortcuts': shortcuts,
            'date-picker-inline': inline,
        }"
    >
        <div ref="datePicker">
            <input
                @blur="blur"
                :disabled="disabled"
                :placeholder="placeholder"
                :class="{ '!cursor-not-allowed': disabled }"
                :name="name"
                class="form-control datepicker"
                type="text"
                data-input
            />
            <button @click.prevent="clearInput" class="btn btn-plain" title="clear" v-if="enableClear" data-clear>
                <i class="fal fa-times fa-fw"></i>
            </button>
        </div>
    </div>
</template>

<script>
import flatpickr from 'flatpickr';
import ShortcutButtonsPlugin from 'shortcut-buttons-flatpickr';
import 'flatpickr/dist/themes/airbnb.css';

export default {
    props: {
        value: {
            required: false,
        },
        start: {
            required: false,
        },
        end: {
            required: false,
        },
        placeholder: {
            type: String,
            default: () => {
                return moment().format('YYYY-MM-DD');
            },
        },
        name: {
            type: String,
            default: 'date',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dateFormat: {
            type: String,
            default: 'Y-m-d',
        },
        mode: {
            type: String,
            default: 'single',
        },
        rangeFormat: {
            type: String,
            default: 'object',
        },
        twelveHourTime: {
            type: Boolean,
            default: false,
        },
        enableClear: {
            type: Boolean,
            default: true,
        },
        enableTime: {
            type: Boolean,
            default: false,
        },
        enableSeconds: {
            type: Boolean,
            default: true,
        },
        firstDayOfWeek: {
            type: Number,
            default: 0,
        },
        inline: {
            type: Boolean,
            default: false,
        },
        events: {
            type: Object,
            default: null,
        },
        config: {
            type: Object,
            default: () => ({}),
        },
        shortcuts: {
            default: false,
        },
    },

    data: () => ({
        flatpickr: null,
        rangeDate: [null, null],
        range: {
            format: 'object',
            divider: ',',
            startKey: 'start',
            endKey: 'end',
        },
    }),

    mounted() {
        let rangeFormat = this.rangeFormat.split(':');
        this.range.format = _.get(rangeFormat, '0', 'object');
        this.range.divider = _.get(rangeFormat, '1', ',');

        this.$nextTick(() => {
            let config = _.defaultsDeep(this.config, {
                wrap: !this.inline,
                inline: this.inline,
                mode: this.mode,
                enableTime: this.enableTime,
                enableSeconds: this.enableSeconds,
                dateFormat: this.dateFormat,
                allowInput: true,
                static: true,
                time_24hr: !this.twelveHourTime,
                locale: { firstDayOfWeek: this.firstDayOfWeek },
                plugins: [],
                onClose: this.onClose,
                onDayCreate: this.onDayCreate,
                onChange: (selectedDates, dateStr, instance) => {
                    this.emitEvent('change', selectedDates, dateStr, instance);
                },
                onMonthChange: (selectedDates, dateStr, instance) => {
                    this.emitEvent('month-change', selectedDates, dateStr, instance);
                },
                onYearChange: (selectedDates, dateStr, instance) => {
                    this.emitEvent('year-change', selectedDates, dateStr, instance);
                },
                onOpen: (selectedDates, dateStr, instance) => {
                    this.emitEvent('open', selectedDates, dateStr, instance);
                },
            });

            // Add shortcut buttons of this.shortcut !== false
            if (this.shortcuts) {
                let buttons = Array.isArray(this.shortcuts)
                    ? this.shortcuts
                    : [
                          {
                              label: 'Today',
                              date: moment(),
                          },
                          {
                              label: 'Last 7 days',
                              date: [moment(), moment().subtract(7, 'days')],
                          },
                          {
                              label: 'Last 30 days',
                              date: [moment(), moment().subtract(30, 'days')],
                          },
                          {
                              label: 'Last 365 days',
                              date: [moment(), moment().subtract(365, 'days')],
                          },
                      ];

                config.plugins.push(
                    ShortcutButtonsPlugin({
                        button: buttons,
                        onClick: (index, fp) => {
                            let date = _.get(buttons[index], 'date', 0);
                            if (Array.isArray(date)) {
                                date = date.map((item) => {
                                    return moment.isMoment(item) ? item.toDate() : item;
                                });
                            } else {
                                date = moment.isMoment(date) ? date.toDate() : date;
                            }
                            fp.setDate(date);
                        },
                    }),
                );
            }

            // Initiate flatpickr
            this.flatpickr = flatpickr(this.$refs.datePicker, config);

            // set value without event first time.
            this.flatpickr.setDate(this.parseRangeInput(this.start && this.end ? [this.start, this.end] : this.value, null), false);

            // turn on all watchers after flatpickr init.
            this.$watch('disabled', this.watchDisabled, {
                immediate: true,
                deep: true,
            });
            this.$watch('value', this.watchValue, { deep: true });
            this.$watch('start', this.watchStart, { deep: true });
            this.$watch('end', this.watchEnd, { deep: true });
            this.$watch('rangeDate', this.watchRangeDate, { deep: true });
        });
    },

    methods: {
        emitEvent(event, selectedDates, dateStr, instance) {
            this.$emit(event, {
                selectedDates: selectedDates,
                dateStr: dateStr,
                instance: instance,
            });
        },
        clearInput() {
            this.onClose(null);
        },
        onDayCreate(dObj, dStr, fp, dayElem) {
            let date = flatpickr.formatDate(dayElem.dateObj, this.dateFormat);

            if (this.events && date in this.events) {
                let className = this.events[date].className ? this.events[date].className : '';
                dayElem.innerHTML += "<span class='date-picker-event " + className + "'></span>";
            }
        },
        onClose(newVal, stringVal, oldVal) {
            if (this.flatpickr.config.mode === 'range') {
                // Format dates for range
                let start = _.get(newVal, '0');
                start = start ? flatpickr.formatDate(start, this.dateFormat) : null;
                let end = _.get(newVal, '1');
                end = end ? flatpickr.formatDate(end, this.dateFormat) : null;
                this.$emit('update:start', start);
                this.$emit('update:end', end);
                newVal = this.formatRangeOutput(start, end);
            } else {
                // Format dates for NOT range.
                newVal = Array.isArray(newVal) ? newVal[0] : newVal;
                newVal = newVal ? flatpickr.formatDate(newVal, this.dateFormat) : null;
            }

            // Only emit if the selection has changed.
            if (!_.isEqual(newVal, this.value)) {
                this.$emit('change', newVal);
                this.$emit('input', newVal);
            }
        },
        formatRangeOutput(start, end) {
            if (this.range.format === 'string') {
                return start + this.range.divider + end;
            }

            return { [this.range.startKey]: start, [this.range.endKey]: end };
        },
        parseRangeInput(newVal, oldVal) {
            if (!newVal) {
                return null;
            }

            if (this.flatpickr.config.mode !== 'range') {
                return newVal;
            }

            if (this.range.format === 'string') {
                let split = _.split(newVal, this.range.divider);
                return [_.get(split, '0'), _.get(split, '1')];
            }

            return [_.get(newVal, this.range.startKey, _.get(newVal, '0')), _.get(newVal, this.range.endKey, _.get(newVal, '1'))];
        },
        watchValue(newVal, oldVal) {
            if (this.flatpickr.config.mode === 'range') {
                newVal = this.parseRangeInput(newVal, oldVal);
            }

            this.flatpickr.setDate(newVal, false);
        },
        watchRangeDate(newVal, oldVal) {
            this.flatpickr.setDate(this.parseRangeInput(newVal, oldVal), false);
        },
        watchStart(newVal) {
            Vue.set(this.rangeDate, '0', newVal);
        },
        watchEnd(newVal) {
            Vue.set(this.rangeDate, '1', newVal);
        },
        watchDisabled(newState) {
            if (newState) {
                this.$refs.datePicker.setAttribute('disabled', newState);
            } else {
                this.$refs.datePicker.removeAttribute('disabled');
            }
        },
        blur() {
            this.$emit('blur');
        },
    },
};
</script>

<style scoped>
.\!cursor-not-allowed {
    cursor: not-allowed !important;
}
</style>
